import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";

// fix leaflet default icon under bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function makePin(color) {
  return L.divIcon({
    className: "",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 4px ${color}40"></div>`,
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [map, points]);
  return null;
}

export default function RouteMap({ start, end, geometry, height = 300, onMapClick, interactive = true, liveLocations = [] }) {
  const center = start ? [start.lat, start.lng] : [55.6761, 12.5683]; // Copenhagen default
  const points = [];
  if (start) points.push([start.lat, start.lng]);
  if (end) points.push([end.lat, end.lng]);
  return (
    <div className="relative border border-[#262626] overflow-hidden" style={{ height }}>
      <MapContainer
        center={center}
        zoom={11}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive}
        attributionControl={true}
        whenCreated={(m) => {
          if (onMapClick) m.on("click", (e) => onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng }));
        }}
      >
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
        {start && <Marker position={[start.lat, start.lng]} icon={makePin("#007AFF")} />}
        {end && <Marker position={[end.lat, end.lng]} icon={makePin("#FF3B30")} />}
        {geometry && geometry.length > 1 && (
          <Polyline positions={geometry} pathOptions={{ color: "#007AFF", weight: 4, opacity: 0.9 }} />
        )}
        {liveLocations && liveLocations.map((l) => (
          <Marker key={l.user_id} position={[l.lat, l.lng]} icon={makeLivePin(l.user_name)} />
        ))}
        <FitBounds points={geometry && geometry.length > 1 ? geometry : points} />
        {onMapClick && <ClickHandler onMapClick={onMapClick} />}
      </MapContainer>
    </div>
  );
}

function makeLivePin(name) {
  const initials = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return L.divIcon({
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `
      <div style="position:relative;width:28px;height:28px;">
        <div style="position:absolute;inset:0;border-radius:50%;background:#34C759;border:2px solid #fff;display:flex;align-items:center;justify-content:center;color:#000;font-size:10px;font-weight:700;">${initials}</div>
        <div style="position:absolute;inset:-6px;border-radius:50%;border:2px solid #34C759;opacity:0.6;animation:pulse-ring 1.6s ease-out infinite;"></div>
      </div>
    `,
  });
}

function ClickHandler({ onMapClick }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !onMapClick) return;
    const fn = (e) => onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.on("click", fn);
    return () => map.off("click", fn);
  }, [map, onMapClick]);
  return null;
}
